import { LoadingButton } from "@mui/lab"
import {
  Button,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  Dialog,
  TextField,
} from "@mui/material"
import {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  DialogTransition,
  parseCSVFromDataUrl,
} from "../../services/utilities/utility"
import DialogTopBar from "../global/dialogTopBar"
import LoadingBackdrop from "../global/loadingBackdrop"
import { useParams } from "react-router-dom"
import { MarketplaceCodesContext } from "../../controllers/marketplaceCodes"
import { AddCircleRounded, DeleteRounded } from "@mui/icons-material"
import FileDropzone from "../global/fileDropzone"

const CreateProductCodeDialog = ({
  dialogOpen,
  setDialogOpen,
  handleDialogChange,
}: {
  dialogOpen: boolean
  setDialogOpen: Dispatch<SetStateAction<boolean>>
  handleDialogChange: MouseEventHandler<HTMLButtonElement>
}) => {
  const { loading, setLoading, createCodes, setCodesList } = useContext(
    MarketplaceCodesContext
  )
  const { productId } = useParams()

  // prevent tab close
  const handleTabClose = useCallback((event: any) => {
    event.preventDefault()
    console.log("beforeunload event triggered")
    return (event.returnValue = "Are you sure you want to exit?")
  }, [])

  useEffect(() => {
    if (dialogOpen) {
      window.addEventListener("beforeunload", handleTabClose)
    } else {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [dialogOpen])

  // data
  const [codes, setCodes] = useState<
    {
      code: string
      inventoryCount: number | null
    }[]
  >([])

  // get data from csv
  const [droppedFile, setDroppedFile] = useState<{
    name: string
    size: number
    type: string
    dataUrl: string
  } | null>(null)

  useEffect(() => {
    if (droppedFile) {
      parseCSVFromDataUrl(droppedFile.dataUrl).then((data) => {
        for (const item of data) {
          if (!item[1] || !isNaN(parseInt(item[1]))) {
            codes.push({
              code: item[0],
              inventoryCount: item[1] ? parseInt(item[1]) : 1,
            })
          }
        }

        setCodes([...codes])
        setDroppedFile(null)
      })
    }
  }, [droppedFile])

  // reset dialog on close
  useEffect(() => {
    if (!dialogOpen) {
      setTimeout(() => {
        setCodes([])
        setDroppedFile(null)
      }, 100)
    }
  }, [dialogOpen])

  // discard dialog
  const [discardDialogOpen, setDiscardDialogOpen] = useState<boolean>(false)

  const handleDiscardDialogCancel = () => {
    setDiscardDialogOpen(false)
  }

  const handleDiscardDialogClose = (e: any) => {
    setDiscardDialogOpen(false)
    handleDialogChange(e)
  }

  // should show discard dialog
  const [showDiscardDialog, setShowDiscardDialog] = useState<boolean>(false)

  useEffect(() => {
    if (codes.some((item) => item.code)) {
      setShowDiscardDialog(true)
    } else {
      setShowDiscardDialog(false)
    }
  }, [codes])

  // create codes
  const createNewCodes = async () => {
    setLoading(true)

    const result = await createCodes(productId, codes)

    if (result) {
      setCodesList((current) => [...result, ...current])
      setDialogOpen(false)
    }

    setLoading(false)
  }

  return (
    <Dialog
      fullScreen
      open={dialogOpen}
      onClose={
        showDiscardDialog
          ? () => {
              setDiscardDialogOpen(true)
            }
          : handleDialogChange
      }
      TransitionComponent={DialogTransition}
    >
      <DialogTopBar
        handleDialogChange={
          showDiscardDialog
            ? () => {
                setDiscardDialogOpen(true)
              }
            : handleDialogChange
        }
        topBarText="Add Codes"
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          variant="outlined"
          style={{
            width: "70%",
            minWidth: 345,
            maxWidth: 850,
            height: 500,
            position: "relative",
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            style={{ paddingTop: 16 }}
          >
            Add Codes
          </Typography>
          <div
            className="horizontal-scroll"
            style={{
              minWidth: 345,
              maxWidth: 850,
              height: "auto",
              display: "flex",
              flexDirection: "row",
              overflow: "hidden",
              scrollSnapType: "x mandatory",
            }}
          >
            <CardContent
              style={{ width: "100%", maxHeight: 370, overflowY: "auto" }}
            >
              <Stack gap={2}>
                {codes.map((item, index) => (
                  <Stack key={index} gap={2} direction="row">
                    <TextField
                      size="small"
                      fullWidth
                      label="Code"
                      value={item.code}
                      onChange={(e) => {
                        codes[index].code = e.target.value
                        setCodes([...codes])
                      }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Inventory amount"
                      value={item.inventoryCount ?? ""}
                      onChange={(e) => {
                        codes[index].inventoryCount = e.target.value
                          ? parseInt(e.target.value)
                          : null
                        setCodes([...codes])
                      }}
                      type="number"
                      inputMode="numeric"
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        setCodes((current) => [
                          ...current.slice(0, index),
                          ...current.slice(index + 1),
                        ])
                      }
                    >
                      <DeleteRounded color="error" />
                    </Button>
                  </Stack>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddCircleRounded />}
                  onClick={() =>
                    setCodes((current) => [
                      ...current,
                      {
                        code: "",
                        inventoryCount: 1,
                      },
                    ])
                  }
                >
                  Add
                </Button>
                <Typography style={{ alignSelf: "center" }}>
                  or upload a CSV file
                </Typography>
                <FileDropzone
                  title="CSV"
                  droppedFile={droppedFile}
                  setDroppedFile={setDroppedFile}
                  csvOnly
                />
              </Stack>
            </CardContent>
          </div>
          <CardActions
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              padding: 16,
              width: "100%",
            }}
          >
            <div style={{ width: "50%" }}>
              <Button
                variant="outlined"
                onMouseDown={(e: any) => {
                  if (showDiscardDialog) {
                    setDiscardDialogOpen(true)
                  } else {
                    handleDialogChange(e)
                  }
                }}
              >
                Close
              </Button>
            </div>
            <div
              style={{
                width: "50%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <LoadingButton
                variant="contained"
                disabled={
                  !codes.length ||
                  codes.some((item) => !item.code) ||
                  codes.some((item) => !item.inventoryCount)
                }
                onMouseDown={createNewCodes}
              >
                Save
              </LoadingButton>
            </div>
          </CardActions>
        </Paper>
      </div>
      <Dialog
        open={discardDialogOpen}
        onClose={handleDiscardDialogCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to close this window?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            All entered data will be lost
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDialogCancel}>Cancel</Button>
          <Button onClick={handleDiscardDialogClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <LoadingBackdrop open={loading} />
    </Dialog>
  )
}

export default CreateProductCodeDialog
